import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {api} from 'utils/api';
import {omit} from 'lodash';
import invariant from 'invariant';

export const defaultQueryIdIndex = 'index';
export const defaultQueryIdById = 'byId';

export const defaultUnwrap = ({data}) => data;

export const defaultUseQueryOptionsBase = {
	refetchOnWindowFocus: false,
	staleTime: 60 * 1000,
};

/**
 * Analyze query error to determine error type
 * @param {Object} error - The error object from react-query
 * @returns {Object} - { hasPermissionError, hasNetworkError, statusCode }
 */
export const analyzeQueryError = error => {
	if (!error) {
		return {
			hasPermissionError: false,
			hasNetworkError: false,
			statusCode: null,
		};
	}

	// Handle both axios errors (error.response.status) and fetch Response errors (error.status)
	// Also handle cases where status might be a string or have additional properties
	let statusCode = error?.response?.status || error?.status;

	const result = {
		hasPermissionError: statusCode === 403,
		hasNetworkError: !statusCode || statusCode >= 500,
		statusCode,
	};

	return result;
};

export const resolveApiPath =
	(pathName = '') =>
	(...pathArgs) => {
		if (!pathArgs.length) return pathName;

		const parts = pathArgs.slice(0, -1);
		const last = pathArgs[pathArgs.length - 1];

		const basePath = parts.reduce((acc, path) => `${acc}/${path}`, pathName);

		if (!last) {
			return basePath;
		}

		if (typeof last === 'object') {
			const params = new URLSearchParams(last).toString();
			return params ? `${basePath}?${params}` : basePath;
		}

		if (last.startsWith && last.startsWith('?')) {
			return `${basePath}${last}`;
		}

		return `${basePath}/${last}`;
	};

export const createApiResource = ({
	name,
	pathName,
	defaultIncludes = [],
	queryIdIndex = defaultQueryIdIndex,
	queryIdById = defaultQueryIdById,
	unwrap = defaultUnwrap,
}) => {
	const getQueryKey = (...args) => [name, ...args];
	const getResourcePath = resolveApiPath(pathName);
	const mergeDefaultParams = (params = {}) => {
		const includes = [...new Set([...defaultIncludes, ...(params.include || [])])].sort();
		return {
			...params,
			...(includes.length ? {include: includes.join(',')} : {}),
		};
	};

	const createQueryClientHelpers = () => {
		const invalidateIndex = (queryClient, params = {}) => {
			queryClient.invalidateQueries({queryKey: getQueryKey(queryIdIndex, params)});
		};

		const invalidateById = (queryClient, id, params = {}) => {
			queryClient.invalidateQueries({queryKey: getQueryKey(queryIdById, id, params)});
		};

		const invalidateAllResourceQueries = queryClient => {
			queryClient.invalidateQueries({queryKey: getQueryKey()});
		};

		const invalidateCustom = (queryClient, queryId, params = {}) => {
			const queryKey =
				Object.keys(params).length === 0
					? getQueryKey(queryId)
					: getQueryKey(queryId, params);
			queryClient.invalidateQueries({queryKey});
		};

		const cancelAllResourceQueries = queryClient => {
			queryClient.cancelQueries({queryKey: getQueryKey()});
		};

		const removeById = (queryClient, id) => {
			queryClient.removeQueries({queryKey: getQueryKey(queryIdById, id)});
		};

		const removeAllResourceQueries = queryClient => {
			queryClient.removeQueries({queryKey: getQueryKey()});
		};

		const setByIdData = (queryClient, id, params, data) => {
			queryClient.setQueryData(getQueryKey(queryIdById, id, params), data);
		};

		const setCustomData = (queryClient, queryId, params, data) => {
			const queryKey =
				Object.keys(params).length === 0
					? getQueryKey(queryId)
					: getQueryKey(queryId, params);
			queryClient.setQueryData(queryKey, data);
		};

		return {
			invalidateIndex,
			invalidateById,
			invalidateCustom,
			invalidateAllResourceQueries,
			cancelAllResourceQueries,
			removeById,
			removeAllResourceQueries,
			setByIdData,
			setCustomData,
		};
	};

	const createUseQueryIndex = ({
		queryId = queryIdIndex,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		const useQueryIndex = function ({params = {}, useQueryOptions = {}} = {}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryFn: () => api.get(getResourcePath(), {params: ps}).then(unwrap),
				queryKey: getQueryKey(queryId, ps),
			});
		};

		// Expose query configuration for direct usage (e.g., in validators)
		useQueryIndex.getQueryKey = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, ps);
		};
		useQueryIndex.getQueryFn = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(), {params: ps}).then(unwrap);
		};

		return useQueryIndex;
	};

	const createUseQueryById = ({
		queryId = queryIdById,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		const useQueryById = function ({id, params = {}, useQueryOptions = {}}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			const enabled =
				!!id &&
				(Object.prototype.hasOwnProperty.call(options, 'enabled')
					? !!options.enabled
					: true);
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				enabled,
				queryKey: getQueryKey(queryId, id, ps),
				queryFn: () => api.get(getResourcePath(id), {params: ps}).then(unwrap),
			});
		};

		// Expose query configuration for direct usage
		useQueryById.getQueryKey = (id, params) => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, id, ps);
		};
		useQueryById.getQueryFn = (id, params) => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(id), {params: ps}).then(unwrap);
		};

		return useQueryById;
	};

	const createUseCustomQuery = ({
		queryId,
		pathName,
		defaultParams = {},
		defaultUseQueryOptions = {},
	} = {}) => {
		invariant(queryId, 'queryId is required');
		invariant(pathName, 'pathName is required');

		const useCustomQuery = function ({params = {}, useQueryOptions = {}} = {}) {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			const options = {
				...defaultUseQueryOptionsBase,
				...defaultUseQueryOptions,
				...useQueryOptions,
			};
			return useQuery({
				...omit(options, ['queryKey', 'queryFn', 'select']),
				queryKey: getQueryKey(queryId, ps),
				queryFn: () => api.get(getResourcePath(pathName), {params: ps}).then(unwrap),
			});
		};

		// Expose query configuration for direct usage
		useCustomQuery.getQueryKey = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return getQueryKey(queryId, ps);
		};
		useCustomQuery.getQueryFn = params => {
			const ps = mergeDefaultParams({...defaultParams, ...params});
			return () => api.get(getResourcePath(pathName), {params: ps}).then(unwrap);
		};

		return useCustomQuery;
	};

	const {invalidateIndex, invalidateById, removeById, cancelAllResourceQueries} =
		createQueryClientHelpers();

	const createUseMutationCreate = ({
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationCreate({useMutationOptions = {}} = {}) {
			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => api.post(getResourcePath(), {body: x}).then(unwrap),
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					invalidateIndex(queryClient);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	const createUseMutationUpdate = ({
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationUpdate({useMutationOptions = {}} = {}) {
			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => api.put(getResourcePath(x.id), {body: x}).then(unwrap),
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					invalidateIndex(queryClient);
					invalidateById(queryClient, x.id);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	const createUseMutationDelete = ({
		defaultUseMutationOptions = {},
		onMutate = () => {},
		onSettled = () => {},
		onError = () => {},
	} = {}) =>
		function useMutationDelete({useMutationOptions = {}} = {}) {
			const queryClient = useQueryClient();
			const options = {
				...defaultUseMutationOptions,
				...useMutationOptions,
			};
			return useMutation({
				...omit(options, ['mutationFn', 'onMutate', 'onSettled', 'onError', 'select']),
				mutationFn: x => {
					return api.remove(getResourcePath(x.id)).then(response => {
						// Handle null/empty responses from DELETE (204 No Content or empty body)
						if (response === null || response === undefined) {
							return null;
						}
						// If response has data property, unwrap it
						if (response.data !== undefined) {
							return unwrap(response);
						}
						// Otherwise return response as-is (already unwrapped or plain object)
						return response;
					});
				},
				onMutate: x => {
					cancelAllResourceQueries(queryClient);
					return onMutate(x, {queryClient});
				},
				onSettled: (_, __, x, ctx) => {
					// Remove the deleted item from cache instead of invalidating (which would try to refetch)
					if (x?.id) {
						removeById(queryClient, x.id);
					}
					// Invalidate index queries to refresh the list
					invalidateIndex(queryClient);
					onSettled(_, __, x, {queryClient, ...ctx});
				},
				onError: (err, x, ctx) => {
					// Remove the deleted item from cache even on error (if deletion partially succeeded)
					if (x?.id) {
						removeById(queryClient, x.id);
					}
					// Still invalidate index queries
					invalidateIndex(queryClient);
					onError(err, x, {queryClient, ...ctx});
				},
			});
		};

	return {
		getQueryKey,
		getResourcePath,
		mergeDefaultParams,
		createUseQueryIndex,
		createUseQueryById,
		createUseCustomQuery,
		createUseMutationCreate,
		createUseMutationUpdate,
		createUseMutationDelete,
		createQueryClientHelpers,
	};
};
